import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Truck, Users, DollarSign, 
  Home, ClipboardList, Star, CheckCircle, Save, Share2, Copy, Check, Download, MessageSquare, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';

const API = process.env.REACT_APP_BACKEND_URL;

const STORAGE_KEY = 'rv_intake_form_draft';

const getInitialFormData = () => {
  // Try to load from localStorage
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (err) {
    console.error('Error loading saved form:', err);
  }
  
  // Default empty form
  return {
    // Contact Info
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    bestNumber: '',
    secondNumber: '',
    canText: '',
    email: '',
    
    // Camping & RV Info
    campingHistory: '',
    towVehicle: '',
    hasHitch: '',
    towCapacity: '',
    sleeps: '',
    kids: '',
    ages: '',
    pets: '',
    nextRvLikesDislikes: '',
    slides: '',
    floorPlan: '',
    length: '',
    nextTripPlanned: '',
    currentRv: '',
    currentPayment: '',
    currentLienholder: '',
    currentLikesDislikes: '',
    
    // Important Features (checkboxes)
    featureSafety: false,
    featurePerformance: false,
    featureAppearance: false,
    featureComfort: false,
    featureEconomy: false,
    featureDependability: false,
    
    // Budget
    monthlyPaymentRange: '',
    initialInvestment: '',
    creditProfile: '',
    
    // Product Selection
    product1: '',
    stock1: '',
    product2: '',
    stock2: '',
    product3: '',
    stock3: '',
    
    // Key Items
    keyItems: '',
  };
};

const CustomerIntakeForm = () => {
  const [formData, setFormData] = useState(getInitialFormData);
  const [lastSaved, setLastSaved] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const formRef = useRef(null);
  const location = useLocation();
  
  // Check if we're on the public route
  const isPublicRoute = location.pathname === '/intake-form';
  const shareUrl = `${window.location.origin}/intake-form`;

  // Auto-save to localStorage whenever form changes
  useEffect(() => {
    const saveTimeout = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
        setLastSaved(new Date());
      } catch (err) {
        console.error('Error saving form:', err);
      }
    }, 500); // Debounce saves by 500ms
    
    return () => clearTimeout(saveTimeout);
  }, [formData]);

  const handleShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success('Link copied! Share with your team.');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard
      toast.error('Could not copy link');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6,10)}`;
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: formatPhoneNumber(value)
    }));
  };

  const handleDownload = () => {
    if (!formData.name) {
      toast.error('Please enter customer name before downloading');
      return;
    }

    // Build features list
    const features = [];
    if (formData.featureSafety) features.push('Safety');
    if (formData.featurePerformance) features.push('Performance');
    if (formData.featureAppearance) features.push('Appearance');
    if (formData.featureComfort) features.push('Comfort');
    if (formData.featureEconomy) features.push('Economy');
    if (formData.featureDependability) features.push('Dependability');

    const timestamp = new Date().toLocaleString();

    // Generate PDF
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 48;
    const maxWidth = pageWidth - margin * 2;
    let y = margin;

    const ensureSpace = (need = 18) => {
      if (y + need > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const writeText = (text, opts = {}) => {
      const { size = 10, style = 'normal', color = [33, 33, 33], indent = 0, lineHeight = 14 } = opts;
      doc.setFont('helvetica', style);
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(text ?? ''), maxWidth - indent);
      for (const ln of lines) {
        ensureSpace(lineHeight);
        doc.text(ln, margin + indent, y);
        y += lineHeight;
      }
    };

    const sectionHeader = (title) => {
      ensureSpace(28);
      y += 8;
      doc.setFillColor(0, 78, 134); // brand navy
      doc.rect(margin, y - 12, maxWidth, 20, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text(title.toUpperCase(), margin + 8, y + 2);
      y += 18;
      doc.setTextColor(33, 33, 33);
    };

    const labelValue = (label, value) => {
      ensureSpace(16);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      doc.text(`${label}:`, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(33, 33, 33);
      const valueX = margin + 130;
      const lines = doc.splitTextToSize(String(value || '—'), maxWidth - 130);
      lines.forEach((ln, i) => {
        ensureSpace(14);
        doc.text(ln, valueX, y);
        if (i < lines.length - 1) y += 14;
      });
      y += 16;
    };

    // Cover
    doc.setFillColor(242, 169, 0); // brand gold
    doc.rect(0, 0, pageWidth, 60, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(0, 78, 134);
    doc.text('RV MATCH AI', margin, 30);
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text('Customer Intake Form', margin, 48);
    y = 88;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated: ${timestamp}`, margin, y);
    y += 18;

    // CONTACT
    sectionHeader('Contact Information');
    labelValue('Name', formData.name);
    labelValue('Address', formData.address);
    labelValue('City / State / Zip', `${formData.city || '—'}, ${formData.state || '—'} ${formData.zipCode || ''}`.trim());
    labelValue('Best Number', formData.bestNumber);
    labelValue('2nd Number', formData.secondNumber);
    labelValue('Text OK?', formData.canText);
    labelValue('Email', formData.email);

    // CAMPING & RV
    sectionHeader('Camping & RV Information');
    labelValue('Camping History', formData.campingHistory);
    labelValue('Tow Vehicle', formData.towVehicle);
    labelValue('Has Hitch?', formData.hasHitch);
    labelValue('Tow Capacity', formData.towCapacity);
    labelValue('Sleeps', formData.sleeps);
    labelValue('Kids', formData.kids);
    labelValue('Ages', formData.ages);
    labelValue('Pets', formData.pets);
    labelValue('Slides', formData.slides);
    labelValue('Floor Plan', formData.floorPlan);
    labelValue('Length', formData.length);
    labelValue('Next Trip Planned', formData.nextTripPlanned);
    labelValue('Current RV', formData.currentRv);
    labelValue('Monthly Payment', formData.currentPayment);
    labelValue('Current Lienholder', formData.currentLienholder);

    // Notes blocks
    if (formData.nextRvLikesDislikes) {
      ensureSpace(20);
      writeText('Next RV — Likes / Dislikes', { size: 9, style: 'bold', color: [80, 80, 80] });
      writeText(formData.nextRvLikesDislikes, { size: 10, lineHeight: 13 });
      y += 4;
    }
    if (formData.currentLikesDislikes) {
      ensureSpace(20);
      writeText('Current RV — Likes / Dislikes', { size: 9, style: 'bold', color: [80, 80, 80] });
      writeText(formData.currentLikesDislikes, { size: 10, lineHeight: 13 });
      y += 4;
    }

    // FEATURES
    sectionHeader('Most Important Features');
    writeText(features.length ? features.join(' • ') : '(None selected)', { size: 11 });

    // BUDGET
    sectionHeader('Budget');
    labelValue('Monthly Payment Range', formData.monthlyPaymentRange);
    labelValue('Initial Investment', formData.initialInvestment);
    labelValue('Credit Profile (1-10)', formData.creditProfile);

    // PRODUCTS
    sectionHeader('Product Selection');
    [1, 2, 3].forEach((i) => {
      const product = formData[`product${i}`];
      const stock = formData[`stock${i}`];
      if (product || stock) {
        labelValue(`#${i}`, `${product || '—'}${stock ? `   |   Stock #: ${stock}` : ''}`);
      }
    });

    // KEY ITEMS
    if (formData.keyItems) {
      sectionHeader('Key Items Important to Guest');
      writeText(formData.keyItems, { size: 10, lineHeight: 13 });
    }

    // Footer on every page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`RV Match AI · Page ${i} of ${pageCount}`, margin, pageHeight - 24);
      doc.text(formData.name || '', pageWidth - margin, pageHeight - 24, { align: 'right' });
    }

    const safeName = formData.name.replace(/[^a-zA-Z0-9]/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];
    doc.save(`Intake_${safeName}_${dateStr}.pdf`);
    toast.success('PDF downloaded!');
  };


  const handleClearForm = () => {
    localStorage.removeItem(STORAGE_KEY);
    setFormData(getInitialFormData());
    setLastSaved(null);
    toast.success('Form cleared');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header - Mobile Optimized */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              {!isPublicRoute && (
                <Link to="/dealer" className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0">
                  <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
                </Link>
              )}
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <ClipboardList className="text-orange-500 flex-shrink-0" size={18} />
                  <span className="truncate">Customer Intake Form</span>
                </h1>
                <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                  <span className="hidden sm:inline">Digital version - </span>Camping World | Good Sam
                  {lastSaved && (
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                      <Save size={12} />
                      <span className="hidden xs:inline">Auto-</span>saved
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {/* Only show Share button on dealer route, not public */}
              {!isPublicRoute && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleShareLink}
                  className={`px-2 sm:px-3 ${linkCopied ? 'bg-green-50 border-green-300 text-green-600' : ''}`}
                  data-testid="share-form-btn"
                >
                  {linkCopied ? <Check size={16} /> : <Share2 size={16} />}
                  <span className="hidden sm:inline ml-1">{linkCopied ? 'Copied!' : 'Share'}</span>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleClearForm} className="px-2 sm:px-3">
                <span className="hidden sm:inline">Clear</span>
                <X size={16} className="sm:hidden" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center backdrop-blur-sm" onClick={() => setShowQRCode(false)}>
          <div 
            className="bg-gradient-to-b from-green-600 to-green-700 w-full sm:max-w-xs sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl relative animate-in slide-in-from-bottom sm:zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button 
              onClick={() => setShowQRCode(false)}
              className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors z-10 active:scale-95"
            >
              <X size={20} className="text-white" />
            </button>
            
            {/* Header */}
            <div className="text-center pt-8 pb-4 px-6">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <MessageSquare size={32} className="text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1">
                Text Us!
              </h3>
              <p className="text-green-100 text-base">
                Scan to opt-in for updates
              </p>
            </div>
            
            {/* QR Code Container */}
            <div className="bg-white mx-4 mb-4 rounded-2xl p-3 shadow-lg">
              <div className="relative">
                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-500 rounded-tl-lg"></div>
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-500 rounded-tr-lg"></div>
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-500 rounded-bl-lg"></div>
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-500 rounded-br-lg"></div>
                
                <img 
                  src="https://customer-assets.emergentagent.com/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/ctcn6ddx_image.png" 
                  alt="Text Us QR Code"
                  className="w-full aspect-square object-contain p-3"
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="bg-green-800/50 py-4 px-6 text-center">
              <p className="text-green-100 text-sm flex items-center justify-center gap-2">
                <span className="inline-block w-2 h-2 bg-green-300 rounded-full animate-pulse"></span>
                Point your camera at the code
              </p>
            </div>
            
            {/* Safe area padding for mobile */}
            <div className="h-6 bg-green-800/50 sm:hidden"></div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Text Us Button - Mobile optimized */}
        <button
          onClick={() => setShowQRCode(true)}
          className="w-full mb-4 sm:mb-6 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-xl p-3 sm:p-4 flex items-center justify-center gap-2 sm:gap-3 transition-colors touch-manipulation"
          data-testid="text-us-btn"
        >
          <MessageSquare size={20} className="sm:w-6 sm:h-6" />
          <span className="font-bold text-base sm:text-lg">Text Us! - Opt-in for Updates</span>
        </button>

        <div className="space-y-4 sm:space-y-6">
          
          {/* Section 1: Contact Information */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <Users size={18} className="text-blue-500" />
              Contact Information
            </h2>
            
            <div className="grid gap-3 sm:gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Customer full name"
                  className="font-medium"
                  data-testid="intake-name"
                  autoComplete="off"
                />
              </div>
              
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Street address"
                  autoComplete="off"
                />
              </div>
              
              {/* City/State/Zip - Stack on mobile, row on tablet+ */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="ST"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="zipCode">Zip</Label>
                  <Input
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    placeholder="Zip"
                  />
                </div>
              </div>
              
              {/* Phone numbers - Stack on mobile */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                <div>
                  <Label htmlFor="bestNumber">Best #</Label>
                  <Input
                    id="bestNumber"
                    name="bestNumber"
                    value={formData.bestNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="secondNumber">2nd #</Label>
                  <Input
                    id="secondNumber"
                    name="secondNumber"
                    value={formData.secondNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    className="text-sm"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label htmlFor="canText">Text OK?</Label>
                  <select
                    id="canText"
                    name="canText"
                    value={formData.canText}
                    onChange={handleChange}
                    className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="customer@email.com"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Camping & RV Information */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <Truck size={18} className="text-green-500" />
              Camping & RV Information
            </h2>
            
            <div className="grid gap-3 sm:gap-4">
              <div>
                <Label htmlFor="campingHistory">Camping History</Label>
                <Input
                  id="campingHistory"
                  name="campingHistory"
                  value={formData.campingHistory}
                  onChange={handleChange}
                  placeholder="e.g., 10 years, new to camping"
                  autoComplete="off"
                />
              </div>
              
              {/* Tow Vehicle section - Mobile optimized */}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3">
                <div className="sm:col-span-2">
                  <Label htmlFor="towVehicle">Tow Vehicle</Label>
                  <Input
                    id="towVehicle"
                    name="towVehicle"
                    value={formData.towVehicle}
                    onChange={handleChange}
                    placeholder="e.g., 2022 Ford F-150"
                    autoComplete="off"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 sm:col-span-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="hasHitch">Hitch?</Label>
                    <select
                      id="hasHitch"
                      name="hasHitch"
                      value={formData.hasHitch}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      autoComplete="off"
                    >
                      <option value="">Select</option>
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="towCapacity">Tow Cap</Label>
                    <Input
                      id="towCapacity"
                      name="towCapacity"
                      value={formData.towCapacity}
                      onChange={handleChange}
                      placeholder="10,000 lbs"
                      className="text-sm"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
              
              {/* Family info - 2x2 grid on mobile, 4 cols on desktop */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <div>
                  <Label htmlFor="sleeps">Sleeps</Label>
                  <Input
                    id="sleeps"
                    name="sleeps"
                    value={formData.sleeps}
                    onChange={handleChange}
                    placeholder="6"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="kids">Kids?</Label>
                  <Input
                    id="kids"
                    name="kids"
                    value={formData.kids}
                    onChange={handleChange}
                    placeholder="2"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="ages">Ages</Label>
                  <Input
                    id="ages"
                    name="ages"
                    value={formData.ages}
                    onChange={handleChange}
                    placeholder="8, 12"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="pets">Pets?</Label>
                  <Input
                    id="pets"
                    name="pets"
                    value={formData.pets}
                    onChange={handleChange}
                    placeholder="2 dogs"
                    autoComplete="off"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="nextRvLikesDislikes">Shopping - Next RV Likes/Dislikes</Label>
                <Textarea
                  id="nextRvLikesDislikes"
                  name="nextRvLikesDislikes"
                  value={formData.nextRvLikesDislikes}
                  onChange={handleChange}
                  placeholder="What are they looking for in their next RV?"
                  rows={2}
                  autoComplete="off"
                />
              </div>
              
              {/* RV specs - 2x2 on mobile */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <div>
                  <Label htmlFor="slides">Slides</Label>
                  <Input
                    id="slides"
                    name="slides"
                    value={formData.slides}
                    onChange={handleChange}
                    placeholder="2"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="floorPlan">Floor Plan</Label>
                  <Input
                    id="floorPlan"
                    name="floorPlan"
                    value={formData.floorPlan}
                    onChange={handleChange}
                    placeholder="Rear living"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="length">Length (ft)</Label>
                  <Input
                    id="length"
                    name="length"
                    value={formData.length}
                    onChange={handleChange}
                    placeholder="32"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="nextTripPlanned">Next Trip</Label>
                  <Input
                    id="nextTripPlanned"
                    name="nextTripPlanned"
                    value={formData.nextTripPlanned}
                    onChange={handleChange}
                    placeholder="June"
                    autoComplete="off"
                  />
                </div>
              </div>
              
              {/* Current RV info - stack on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <Label htmlFor="currentRv">Current RV</Label>
                  <Input
                    id="currentRv"
                    name="currentRv"
                    value={formData.currentRv}
                    onChange={handleChange}
                    placeholder="e.g., 2018 Keystone Cougar"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="currentPayment">Monthly Payment</Label>
                  <Input
                    id="currentPayment"
                    name="currentPayment"
                    value={formData.currentPayment}
                    onChange={handleChange}
                    placeholder="$450"
                    autoComplete="off"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="currentLienholder">Current Lienholder</Label>
                <Input
                  id="currentLienholder"
                  name="currentLienholder"
                  value={formData.currentLienholder}
                  onChange={handleChange}
                  placeholder="e.g., Bank of America"
                  autoComplete="off"
                />
              </div>
              
              <div>
                <Label htmlFor="currentLikesDislikes">Current RV Likes/Dislikes</Label>
                <Textarea
                  id="currentLikesDislikes"
                  name="currentLikesDislikes"
                  value={formData.currentLikesDislikes}
                  onChange={handleChange}
                  placeholder="What do they like/dislike about their current RV?"
                  rows={2}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Most Important Features */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <Star size={18} className="text-yellow-500" />
              Most Important Features
            </h2>
            
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">Select all that apply:</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {[
                { name: 'featureSafety', label: 'Safety', icon: '🛡️' },
                { name: 'featurePerformance', label: 'Performance', icon: '⚡' },
                { name: 'featureAppearance', label: 'Appearance', icon: '✨' },
                { name: 'featureComfort', label: 'Comfort', icon: '🛋️' },
                { name: 'featureEconomy', label: 'Economy', icon: '💰' },
                { name: 'featureDependability', label: 'Dependability', icon: '🔧' },
              ].map((feature) => (
                <label
                  key={feature.name}
                  className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData[feature.name]
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={feature.name}
                    checked={formData[feature.name]}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <span className="text-base sm:text-lg">{feature.icon}</span>
                  <span className={`text-xs sm:text-sm font-medium ${formData[feature.name] ? 'text-orange-600 dark:text-orange-400' : 'text-gray-700 dark:text-gray-300'}`}>
                    {feature.label}
                  </span>
                  {formData[feature.name] && <CheckCircle size={14} className="text-orange-500 ml-auto hidden sm:block" />}
                </label>
              ))}
            </div>
          </div>

          {/* Section 4: Budget Information */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-green-500" />
              Budget Information
            </h2>
            
            <div className="grid gap-3 sm:gap-4">
              <div>
                <Label htmlFor="monthlyPaymentRange">Monthly Payment Range</Label>
                <select
                  id="monthlyPaymentRange"
                  name="monthlyPaymentRange"
                  value={formData.monthlyPaymentRange}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">Select range...</option>
                  <optgroup label="Travel Trailer (TT)">
                    <option value="TT: $550-600">TT: $550-600/mo</option>
                    <option value="TT: $600-650">TT: $600-650/mo</option>
                  </optgroup>
                  <optgroup label="Fifth Wheel (FW)">
                    <option value="FW: $850-900">FW: $850-900/mo</option>
                    <option value="FW: $900-950">FW: $900-950/mo</option>
                  </optgroup>
                  <optgroup label="Motorhome (MH)">
                    <option value="MH: $1,150-1,250">MH: $1,150-1,250/mo</option>
                    <option value="MH: $1,250-1,350">MH: $1,250-1,350/mo</option>
                  </optgroup>
                  <option value="Custom">Other (specify in notes)</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="initialInvestment">Down Payment</Label>
                <select
                  id="initialInvestment"
                  name="initialInvestment"
                  value={formData.initialInvestment}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">Select range...</option>
                  <optgroup label="Travel Trailer (TT)">
                    <option value="TT: $5,000-10,000">TT: $5,000-10,000</option>
                  </optgroup>
                  <optgroup label="Fifth Wheel (FW)">
                    <option value="FW: $15,000-20,000">FW: $15,000-20,000</option>
                  </optgroup>
                  <optgroup label="Motorhome (MH)">
                    <option value="MH: $30,000-40,000">MH: $30,000-40,000</option>
                  </optgroup>
                  <option value="Custom">Other (specify in notes)</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="creditProfile">Credit Profile (1-10)</Label>
                <div className="grid grid-cols-5 sm:flex sm:flex-wrap gap-1 sm:gap-2">
                  {[1,2,3,4,5,6,7,8,9,10].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, creditProfile: num.toString() }))}
                      className={`h-9 sm:w-10 sm:h-10 rounded-lg border-2 font-bold transition-all text-sm ${
                        formData.creditProfile === num.toString()
                          ? 'border-orange-500 bg-orange-500 text-white'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Product Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
              <Home size={18} className="text-purple-500" />
              Product Selection
            </h2>
            
            <div className="space-y-3">
              {[1, 2, 3].map((num) => (
                <div key={num} className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-end">
                  <div className="flex-1">
                    <Label htmlFor={`product${num}`}>#{num} Product</Label>
                    <Input
                      id={`product${num}`}
                      name={`product${num}`}
                      value={formData[`product${num}`]}
                      onChange={handleChange}
                      placeholder="e.g., 2024 Keystone Montana"
                      className="text-sm"
                      autoComplete="off"
                    />
                  </div>
                  <div className="w-full sm:w-28">
                    <Label htmlFor={`stock${num}`} className="sm:sr-only">Stock #</Label>
                    <Input
                      id={`stock${num}`}
                      name={`stock${num}`}
                      value={formData[`stock${num}`]}
                      onChange={handleChange}
                      placeholder="Stock #"
                      className="text-sm"
                      autoComplete="off"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 6: Key Items */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Key Items Important to Guest
            </h2>
            <Textarea
              id="keyItems"
              name="keyItems"
              value={formData.keyItems}
              onChange={handleChange}
              placeholder="Notes about what's most important to this customer..."
              rows={3}
              className="text-sm"
              autoComplete="off"
            />
          </div>

          {/* Section 7: Download */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 sm:p-6 text-white">
            <h2 className="text-base sm:text-lg font-bold mb-2 flex items-center gap-2">
              <Download size={18} />
              Download Form
            </h2>
            <p className="text-xs sm:text-sm opacity-90 mb-3 sm:mb-4">
              Generate a polished PDF copy of this intake form to email yourself, share with the customer, or attach to the deal.
            </p>

            <Button
              type="button"
              onClick={handleDownload}
              className="w-full bg-white text-orange-600 hover:bg-gray-100 font-semibold py-2.5 sm:py-3 text-sm sm:text-base"
              data-testid="download-form-btn"
            >
              <Download size={16} className="mr-2" />
              Download PDF
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CustomerIntakeForm;
